import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Calendar, Clock, User, Mail, Phone, Check, X, RefreshCw, Loader2 } from 'lucide-react';

interface Booking {
  id: string;
  name: string;
  email: string;
  phone: string;
  date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  created_at: string;
}

export function AdminDashboard() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [proposedTime, setProposedTime] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching bookings:', error);
      return;
    }

    setBookings(data || []);
    setLoading(false);
  };

  const handleStatusUpdate = async (bookingId: string, status: 'confirmed' | 'cancelled') => {
    const { error } = await supabase
      .from('bookings')
      .update({ status })
      .eq('id', bookingId);

    if (error) {
      console.error('Error updating booking:', error);
      return;
    }

    // Send email notification based on status
    const booking = bookings.find(b => b.id === bookingId);
    if (booking) {
      await sendStatusEmail(booking, status);
    }

    fetchBookings();
  };

  const handleProposeNewTime = async (bookingId: string) => {
    if (!proposedDate || !proposedTime) return;

    const { error } = await supabase
      .from('bookings')
      .update({
        date: proposedDate,
        start_time: proposedTime,
        end_time: new Date(`2000-01-01T${proposedTime}`).getTime() + 15 * 60000,
        status: 'pending'
      })
      .eq('id', bookingId);

    if (error) {
      console.error('Error proposing new time:', error);
      return;
    }

    // Send email notification about new proposed time
    const booking = bookings.find(b => b.id === bookingId);
    if (booking) {
      await sendProposedTimeEmail(booking, proposedDate, proposedTime);
    }

    setProposedDate('');
    setProposedTime('');
    setSelectedBooking(null);
    fetchBookings();
  };

  const sendStatusEmail = async (booking: Booking, status: string) => {
    const { error } = await supabase.functions.invoke('send-booking-email', {
      body: {
        to: booking.email,
        subject: `Booking ${status === 'confirmed' ? 'Confirmed' : 'Cancelled'}`,
        booking,
        status
      }
    });

    if (error) {
      console.error('Error sending email:', error);
    }
  };

  const sendProposedTimeEmail = async (booking: Booking, newDate: string, newTime: string) => {
    const { error } = await supabase.functions.invoke('send-booking-email', {
      body: {
        to: booking.email,
        subject: 'New Time Proposed for Your Booking',
        booking,
        newDate,
        newTime
      }
    });

    if (error) {
      console.error('Error sending email:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h2 className="text-xl font-semibold mb-4">Booking Management</h2>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td className="px-4 py-3 text-sm">{booking.name}</td>
                <td className="px-4 py-3 text-sm">
                  <div>{booking.email}</div>
                  <div>{booking.phone}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div>{new Date(booking.date).toLocaleDateString()}</div>
                  <div>{booking.start_time} - {booking.end_time}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {booking.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {booking.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleStatusUpdate(booking.id, 'confirmed')}
                        className="p-1 text-green-600 hover:text-green-800"
                        title="Confirm"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(booking.id, 'cancelled')}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSelectedBooking(booking)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Propose New Time"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Propose New Time Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Propose New Time</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Date</label>
                <input
                  type="date"
                  value={proposedDate}
                  onChange={(e) => setProposedDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Time</label>
                <input
                  type="time"
                  value={proposedTime}
                  onChange={(e) => setProposedTime(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleProposeNewTime(selectedBooking.id)}
                  className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-accent"
                >
                  Send Proposal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}